import { Box, CircularProgress } from "@mui/material";
import { DeleteDialog } from "@sagecentral/core-ui";
import { IAddNotePayload, INote, INotePayload, MarkReadSignature, NoteDrawerMode } from "models/notes";
import useDeleteNote from "hooks/notes/useDeleteNote";
import { useState, useRef } from "react";
import NoteCard from "./NoteCard";
import NoteListToolbar from "./NoteListToolbar";
import NoteEdit from "./NoteEdit";

type NoteListProps = {
    loading: boolean;
    notes: INote[];
    draft?: boolean;
    onDelete?: () => Promise<void>;
    onMarkRead?: MarkReadSignature;
    onRefresh: () => void;
    onAdd: (payload: IAddNotePayload) => void;
    onUpdate: (noteId: number, payload: INotePayload, draft?: boolean) => void;
};

type DrawerState = {
    mode: NoteDrawerMode;
    open: boolean;
}

const NoteList: React.FC<NoteListProps> = (props) => {
    const {
        loading,
        notes,
        draft,
        onRefresh
    } = props;
    
    // Reference to the toolbar button for focus management
    const toolbarRef = useRef<HTMLButtonElement | null>(null);

    const defaultHandleDelete = useDeleteNote();
    /**
     * Handles the deletion of a note and moves focus to toolbar button
     */
    const handleDelete = (noteId: number) => {
        const handler = props.onDelete ?? defaultHandleDelete;
        (async () => {
            await handler(noteId);
            setShowDeleteConfirm(false);
            
            // Focus the add button in the toolbar after deletion
            setTimeout(() => {
                if (toolbarRef.current) {
                    toolbarRef.current.focus();
                }
            }, 0);
            
            onRefresh();
        })();
    } 

    const [ showDeleteConfirm, setShowDeleteConfirm ] = useState<boolean>(false);
    const [ targetNote, setTargetNote ] = useState<INote | null>(null);
    const [ drawerState, setDrawerState ] = useState<DrawerState>({
        mode: 'add',
        open: false
    });

    const handleReplyClick = (note: INote) => {
        setTargetNote(note ?? null);
        setDrawerState({
            mode: 'reply',
            open: true
        });
    };

    const handleEditClick = (note: INote) => {
        setTargetNote(note ?? null);
        setDrawerState({
            mode: 'edit',
            open: true
        });
    };
    
    const handleDeleteClick = (note: INote) => {
        setTargetNote(note ?? null);
        setShowDeleteConfirm(true);
    };

    const handleAddClick = () => {
        setTargetNote(null);
        setDrawerState({
            mode:'add',
            open: true
        });
    };

    const handleSave = (mode: NoteDrawerMode, payload: INotePayload | IAddNotePayload, noteId?: number) => {
        switch (mode) {
            case 'add':
            case 'reply':
                props.onAdd(payload);
                break;
            default: 
                props.onUpdate(noteId ?? -1, payload as INotePayload, draft);
                break;
        }
        setDrawerState({
            ...drawerState,
            open: false
        });
    }

    if (loading) {
        return <CircularProgress sx={{ position: 'absolute' }} color="primary" />;
    } 

    if (notes.length === 0) {
        return (
        <>                        
            {drawerState.open && drawerState.mode === 'add' &&
            <>
                <NoteListToolbar onAddClick={handleAddClick} />
                <NoteEdit 
                    mode={drawerState.mode}
                    sourceNote={targetNote ?? null}
                    onClose={() => setDrawerState({
                        ...drawerState,
                        open: false
                    })}
                    draft={draft}
                    onSave={handleSave}
                />
            </>
            ||
            <>
                <i>Would you like to add a note?</i>
                <NoteListToolbar onAddClick={handleAddClick} />
            </>
            }            
        </>);
    }

    return (<>     
        <NoteListToolbar ref={toolbarRef} onAddClick={handleAddClick} />   
        <>
            {drawerState.open && drawerState.mode === 'add' &&
                <NoteEdit 
                    mode={drawerState.mode}
                    sourceNote={targetNote ?? null}
                    onClose={() => setDrawerState({
                        ...drawerState,
                        open: false
                    })}
                    draft={draft}
                    onSave={handleSave}
                />
            }
        </>
        <Box sx={{
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}>        
            { notes.map((note, index) => (
                <NoteCard note={note} key={index} allowReply
                    onReplyClick={handleReplyClick}
                    onEditClick={handleEditClick}
                    onDeleteClick={handleDeleteClick}
                    mode={drawerState.mode}
                    open={drawerState.open}
                    sourceNote={targetNote ?? null}
                    onClose={() => setDrawerState({
                        ...drawerState,
                        open: false
                    })}
                    onSave={handleSave}
                    draft={draft}
                />
            ))}
        </Box>        
        <DeleteDialog nameOfItem="Note" 
            open={showDeleteConfirm} 
            onCancel={() => setShowDeleteConfirm(false)}
            onDelete={() => {                
                handleDelete(targetNote?.id ?? -1);
            }}
        />
    </>);
};

export default NoteList;
